/**
 * Email Customization API Service — Product Portal
 *
 * Endpoints via tms-api-onboarding (port 5008):
 *   GET  /email-templates            — list all active templates
 *   PUT  /email-templates/{key}      — update & persist a template
 *   POST /email-templates/{key}/preview — render / send a test email
 */
import { getApiClient } from '@/core/services/api.service';
import { API_ROUTES } from '@/core/config';
import type {
  EmailTemplate,
  UpdateEmailTemplatePayload,
  EmailTemplatePreviewPayload,
  EmailTemplatePreviewResponse,
} from '../types';

const api = getApiClient();

/**
 * Fetch all active email templates.
 * GET /onboarding/api/v1/email-templates
 */
export async function fetchEmailTemplates(): Promise<EmailTemplate[]> {
  const response = await api.get<EmailTemplate[]>(
    `${API_ROUTES.ONBOARDING}/email-templates`,
  );
  return response.data;
}

/**
 * Update and persist an email template.
 * PUT /onboarding/api/v1/email-templates/{templateKey}
 */
export async function updateEmailTemplate(
  templateKey: string,
  payload: UpdateEmailTemplatePayload,
): Promise<EmailTemplate> {
  const response = await api.put<EmailTemplate>(
    `${API_ROUTES.ONBOARDING}/email-templates/${encodeURIComponent(templateKey)}`,
    payload,
  );
  return response.data;
}

/**
 * Preview a rendered email template (optionally send a test email).
 * POST /onboarding/api/v1/email-templates/{templateKey}/preview
 */
export async function previewEmailTemplate(
  templateKey: string,
  payload: EmailTemplatePreviewPayload,
): Promise<EmailTemplatePreviewResponse> {
  const response = await api.post<EmailTemplatePreviewResponse>(
    `${API_ROUTES.ONBOARDING}/email-templates/${encodeURIComponent(templateKey)}/preview`,
    payload,
  );
  return response.data;
}
